import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BeatLoader } from "react-spinners";
import PageHeroSection from "../../components/ui/PageSections/PageHeroSection";
import { usePageSettings } from "../../contexts/PageSettingsContext";
import iconWebHosting from "../../images/icons/icon-web-hosting.svg";
import useFetchWebHostingPlans from "../../hooks/useFetchWebHostingPlans";
import EmpowerServicesSection from "../../components/ui/PageSections/EmpowerServicesSection";
import useRedirect from "../../hooks/useRedirect";
import Section from "../../components/ui/Section";
import PresenceBanner from "../../components/ui/PageSections/PresenceBanner";
import ContactUsBanner from "../../components/ui/PageSections/ContactUsBanner";
import { useSelectedPlanContext } from "../../contexts/SelectedPlanContext";
import { FaCheckCircle } from "react-icons/fa";
import PrimaryButton from "../../components/ui/inputs/buttons/Button";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { constructUrl } from "../../utils/constructUrl";
import { getTranslatedCityName } from "../../utils/getTranslatedCityName";
import { navigate } from "gatsby";

const WebHostingPageTemplate = ({ pageContext, translations }) => {
	const { selectedLocation, currency } = usePageSettings();
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();
	const { redirecting, redirect } = useRedirect();
	const queryWebHostingPlans = useFetchWebHostingPlans();

	const handleOrderNow = (event, planId) => {
		event.preventDefault();
		redirect("webhosting", selectedLocation.iata_code, selectedPlan, planId);
	};

	const { city, language, country, hasCityInUrl, isDf, isHomepage } = pageContext;
	let citySlug = hasCityInUrl ? getTranslatedCityName(language, city) : null;
	let countrySlug = isDf ? "df" : isHomepage ? null : country;
	const checkoutUrl = constructUrl(language, countrySlug, citySlug, "/checkout");

	const setWebhostingPlan = (event, plan, planFeatures) => {
		event.preventDefault();

		changeSelectedPlan((prevState) => ({
			...prevState,
			service: plan.service,
			id: plan.id,
			name: plan.name,
			type: plan.service,
			price: plan.price,
			webFeatures: planFeatures,
		}));

		// Navigate to checkout
		const checkoutUrl = constructUrl(language, countrySlug, citySlug, "/checkout");
		// debugger;
		sessionStorage.removeItem("checkout-tab");
		navigate(checkoutUrl);
	};

	return (
		<section>
			<PageHeroSection
				intro={translations["WebHostIntro"]}
				heroIcon={iconWebHosting}
				heroTitle={translations["WebHostMainTitle"] ? translations["WebHostMainTitle"] : pageContext.h1Title}
				subtitle={translations["WebHostSubtitle"] ? translations["WebHostSubtitle"] : pageContext.h2Title}
			/>
			<section className="md:container md:mx-auto pt-10 pb-0">
				<div className="flex w-full">
					{/* Other code... */}
					{queryWebHostingPlans.isFetching ? (
						<div className="h-96 flex flex-col gap-2 w-full flex-grow items-center justify-center">
							<BeatLoader color="#d1d5db" size={12} />
						</div>
					) : (
						<AnimatedList>
							{queryWebHostingPlans.isSuccess ? (
								<>
									{queryWebHostingPlans.data
										?.sort((a, b) => a.order - b.order)
										.map((plan, index) => (
											<AnimatedItem key={plan.id} index={index}>
												<Plan
													plan={plan}
													translations={translations}
													handleOrderNow={handleOrderNow}
													redirecting={redirecting}
													currency={currency}
													setWebhostingPlan={setWebhostingPlan}
												/>
											</AnimatedItem>
										))}
								</>
							) : (
								<div className="flex justify-center bg-gray-50 py-8 px-6 rounded-lg mx-auto my-20">
									<ErrorMessage translations={translations} />
								</div>
							)}
						</AnimatedList>
					)}
				</div>
			</section>
			<Section color="transparent">
				<EmpowerServicesSection translations={translations} />
			</Section>
			<Section>
				<PresenceBanner translations={translations} />
			</Section>
			<Section color="transparent">
				<ContactUsBanner
					title={translations["WebHostContactUsBannerTitle"]}
					subtitle={translations["WebHostContactUsBannerSubtitle"]}
					contactText={translations["contact"]}
				/>
			</Section>
		</section>
	);
};

export default WebHostingPageTemplate;

const Plan = ({ plan, setWebhostingPlan, redirecting, translations }) => {
	const personalPlanFeatures = [
		"WebHostPersonalPlanWebSiteValue",
		"WebHostPersonalPlanStorageValue",
		"WebHostPersonalPlanEmailAccountsValue",
		"WebHostPersonalPlanFeatureTwo",
		"WebHostPersonalPlanFeatureOne",
		"WebHostPersonalPlanFeatureFour",
	];
	const corporatePlanFeatures = [
		"WebHostCorporativePlanWebSiteValue",
		"WebHostCorporativePlanStorageValue",
		"WebHostCorporativePlanEmailAccountsValue",
		"WebHostCorporativePlanFeatureTwo",
		"WebHostCorporativePlanFeatureOne",
		"WebHostCorporativePlanFeatureFour",
	];
	const multidomainPlanFeatures = [
		"WebHostMultidomainPlanWebSiteValue",
		"WebHostMultidomainPlanStorageValue",
		"WebHostMultidomainPlanEmailAccountsValue",
		"WebHostMultidomainPlanFeatureTwo",
		"WebHostMultidomainPlanFeatureOne",
		"WebHostMultidomainPlanFeatureFour",
	];

	let planFeatures = [];

	switch (plan.name) {
		case "planpersonal":
			planFeatures = personalPlanFeatures;
			break;
		case "plancorporate":
			planFeatures = corporatePlanFeatures;
			break;
		case "planmultidomain":
			planFeatures = multidomainPlanFeatures;
			break;
		default:
			planFeatures = personalPlanFeatures;
			break;
	}

	return (
		<div className="lg:w-2/3 gap-8 flex flex-col justify-around lg:py-8 text-ipxon-black">
			<h3 className="ipxon-magenta-text-gradient font-bold text-lg">{translations[plan.name]}</h3>
			<ul className="flex flex-col gap-3">
				{planFeatures.map((feature, index) => (
					<li key={index} className="flex gap-2 items-start text-sm">
						<span className="pt-[2.5px]">
							<FaCheckCircle className="h-3 w-3 text-[#B9BFC1]" />
						</span>

						<span className="flex items-center">{translations[feature]}</span>
					</li>
				))}
			</ul>
			<div className="font-bold text-3xl items-end flex gap-2">
				<span className="flex gap-0 uppercase">{plan.price}</span>
				<span className="text-xs font-light flex flex-col justify-center">/{translations["per year"]}</span>
			</div>
			<div className="flex w-full justify-start">
				<PrimaryButton
					disabled={redirecting}
					onClick={(event) => setWebhostingPlan(event, plan, planFeatures)}
					className={`button-magenta flex reflect-below w-[175px] self-start gap-1 h-8 ${
						redirecting && "cursor-not-allowed"
					}`}
				>
					{redirecting ? <BeatLoader color="#d1d5db" size={8} /> : translations["InputTableButtonText"]}
				</PrimaryButton>
			</div>
		</div>
	);
};

const AnimatedList = ({ children }) => {
	const motionContainer = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	};
	return (
		<AnimatePresence>
			<motion.ul
				initial="hidden"
				animate="visible"
				exit="hidden"
				variants={motionContainer}
				className="w-full flex flex-col lg:flex-row gap-10 lg:gap-4 xl:gap-16 2xl:gap-40 items-center justify-beetween px-6 lg:px-0"
			>
				{children}
			</motion.ul>
		</AnimatePresence>
	);
};

const AnimatedItem = ({ children, index }) => {
	const motionItem = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 },
	};
	return (
		<motion.li
			variants={motionItem}
			initial={{ opacity: 0 }} // Initial position and opacity
			animate={{ opacity: 1, y: 0 }} // Final position and opacity
			transition={{ duration: 0.5, delay: index * 0.2 }} // Staggered delay based on index
			className="bg-gray-50 rounded-lg justify-center flex flex-grow p-8 lg:p-0 w-full sm:max-w-sm xl:max-w-none"
		>
			{children}
		</motion.li>
	);
};
